import { FormikErrors, FormikTouched } from "formik";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";

import { Container, Row, useToast } from "../../components";
import { Location, Space } from "../../data";
import { MembershipDetail, PartnerOffering } from "../../data/partner_offering";
import {
  buildPathWithParams,
  useMediaQuery,
  useQueryString,
} from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import {
  MembershipPolicies,
  OfferingMessageType,
  Order,
  OrderStatus,
  Quote,
  useOrderQueryCallback,
  usePartnerPaymentConfigsQuery,
} from "../../offering/api";
import { ContactFormProvider } from "../../offering/contact_form_provider";
import { Hidden } from "../../offering/hidden";
import { useSyncQueryStringV2 } from "../../offering/offering_helpers";
import { OrderParams } from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import { Spacer } from "../../offering/spacer";
import { CheckoutStripeInput } from "../../offering/template_offering_checkout_stripe_base";
import {
  BackButton,
  BillingDetails,
  FooterV2,
  GuestDetails,
  OfferingHeader,
  PaymentCTA,
  PaymentCTAWithMembershipTerms,
} from "../components";
import { TalkWithUsPrompt } from "../components/talk_with_us_prompt";
import {
  useAppleGooglePayEmbedInCheckoutPage,
  useApplePayFixFeatureFlag,
  useEnhanceCheckoutUX,
  useIncludeAllPaymentsFeatureFlag,
  usePaymentModalFeatureFlag,
} from "../../core/splitio";
import PaymentElementInput, {
  PaymentElementInputActions,
} from "../../core/payment_element_input";
import { StripeProviderV2 } from "../../core/stripe";
import { useHistory } from "react-router-dom";
import { LoadingOverlay } from "../../offering/loading_overlay";
import { Form, Modal } from "react-bootstrap";
import { PulseLoader } from "../../components/pulse_loader";
import { isEmpty } from "lodash";
import * as Sentry from "@sentry/browser";
import { CancellationPolicy } from "../components/cancellation_policy";
import { useTrackMembershipPurchase } from "../../offering/analytics_ecommerce_events";
import { showPaymentForm } from "../utils/show_payment_form";
import { useAnalytics } from "@/lib/analytics/analytics";
import { getCheckoutErrorFieldId } from "../utils/get_checkout_error_field_id";
import { useFadeInErrors } from "../hooks/use_fade_in_errors";
import { usePrevious } from "@/hooks/use_previous";
import { getPartnerOfferingName } from "@/offering/get_partner_offering_name";
import { MarketingConsent } from "@/offering/marketing_consent";
import { Payments } from "../components/payments";
import { colors } from "../components/colors";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { EquinoxMembershipNumber } from "../components/equinox_memberhsip_number";
import { MALIN_PARTNER_UUIDS } from "@/offering/themalin/themalin_partner_config";

interface TemplateOfferingCheckoutStripeMembershipBaseProps {
  partnerConfig: PartnerConfig;
  disabledCheckout: boolean;
  offeringName: string;
  partnerOfferingSlug: string;

  // Data
  includeQuantity: boolean | undefined;
  quote: Quote | undefined;
  space: Space | undefined;
  location: Location | undefined;
  partnerOffering: PartnerOffering;
  askForTax: boolean;
  membershipPolicy: MembershipPolicies | undefined;

  // Payment
  order: Order | undefined;
  onPaymentClose: () => void;

  // Checkout
  orderParams: OrderParams;
  form: {
    values: CheckoutStripeInput;
    errors: FormikErrors<CheckoutStripeInput>;
    touched: FormikTouched<CheckoutStripeInput>;
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => any;
    setFieldTouched: (
      field: string
    ) => Promise<void> | Promise<FormikErrors<CheckoutStripeInput>>;
    setFieldError: (field: string, value: string | undefined) => void;
    submit: () => Promise<void>;
    submitting: boolean;
    onCheckContactDetailsSameAsBilling: (value: boolean) => void;
  };
  promotion?: React.ReactNode;
  validateOrderParams: () => boolean;
  onPressBack: () => void;
  onChangeEmail: (email?: string) => void;
  mobileCheckoutBox: React.ReactNode;
  desktopCheckoutBox: React.ReactNode;

  // Membership
  membership?: MembershipDetail;
  offeringLocationID?: string;
}

const STRIPE_POLLING_TIMEOUT_IN_SECOND = 60;

export function TemplateOfferingCheckoutStripeMembershipBase(
  props: TemplateOfferingCheckoutStripeMembershipBaseProps
) {
  const {
    partnerConfig,
    partnerOfferingSlug,
    offeringName,
    quote,
    disabledCheckout,
    partnerOffering,
    order,
    onPaymentClose,
    askForTax,
    membershipPolicy,
    orderParams,
    form,
    validateOrderParams,
    onPressBack,
    location,
    mobileCheckoutBox,
    desktopCheckoutBox,
    onChangeEmail,

    membership,
  } = props;
  const {
    hubspotForm,
    hubspotForms,
    favicon,
    termsURL,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    localeOptions,
    instagramLink,
  } = partnerConfig;

  const {
    values,
    errors: formErrors,
    setFieldValue,
    submit,
    submitting,
    touched,
    setFieldTouched,
    setFieldError,
    onCheckContactDetailsSameAsBilling,
  } = form;
  const [emailError, setEmailError] = useState<string>();
  const errors = useMemo(() => {
    return { ...formErrors, email: formErrors.email || emailError };
  }, [formErrors, emailError]);
  useSyncQueryStringV2(orderParams);
  const appleFixFeatureFlag = useApplePayFixFeatureFlag(
    partnerOffering.partner.id
  );
  const queryParams = useQueryString();
  const isEnhanceCheckoutUXFlagEnabled = useEnhanceCheckoutUX();
  const includeAllPaymentsFeatureFlag = useIncludeAllPaymentsFeatureFlag();
  const paymentModalFeatureFlag = usePaymentModalFeatureFlag();

  const hasFormErrors = !!Object.keys(errors).length;
  const analytics = useAnalytics();

  const onTermsClick = useCallback(() => {
    analytics.event(
      "Checkout Terms Click",
      {
        "Partner Name": partnerOffering.partner.name,
        "Partner Internal Name": partnerOffering.partner.internalName,
        "Product Name": getPartnerOfferingName(partnerOffering),
        "Product Category":
          membership?.productCategory || membership?.productTypeName,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [
    analytics,
    membership?.productCategory,
    membership?.productTypeName,
    partnerOffering,
  ]);
  const [paymentType, setPaymentType] = useState<string>("card");
  const quotePositivePrice = quote ? quote.totalPrice > 0 : false;
  const mq = useMediaQuery();
  const { fbt } = useI18n();
  const toast = useToast();
  const getOrder = useOrderQueryCallback();
  const history = useHistory();
  const trackMembershipPurchase = useTrackMembershipPurchase();
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerOffering.partner.id);
  const [googleApplePayPaymentSucceeded, setGoogleApplePayPaymentSucceeded] =
    useState(false);
  const isPaymentElementOpen = !isGoogleApplePayEmbedInCheckoutPage && !!order;
  const paymentElementRef = useRef<PaymentElementInputActions>();
  const completedRef = useRef(false);
  const redirectUrl = order
    ? `${window.location.origin}${buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      )}`
    : "";
  const errorElementRef = useRef<HTMLDivElement>(null);
  const errorFieldId = getCheckoutErrorFieldId(errors, touched);
  const { handleErrorTextRef, handleInputBorderRef, fadeInErrors } =
    useFadeInErrors(errors, touched);

  const { data: paymentConfig } = usePartnerPaymentConfigsQuery(
    partnerOffering.partner.id,
    location?.id
  );

  useEffect(() => {
    if (membershipPolicy?.marketingConsentCheckedByDefault) {
      setFieldValue("marketingConsent", true);
    }
  }, [membershipPolicy?.marketingConsentCheckedByDefault, setFieldValue]);

  const prevOrder = usePrevious(order);
  // we use ref so that all callbacks will receive the latest order
  const orderRef = useRef(order);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  useEffect(() => {
    if (
      isGoogleApplePayEmbedInCheckoutPage &&
      quote &&
      quote.totalPrice > 0 &&
      isEmpty(quote.paymentClientSecret)
    ) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          partnerId: partnerOffering.partner.id,
          offeringId: partnerOffering.id,
          offeringSlug: partnerOffering.slug,
        });
        Sentry.captureMessage(
          `WL: Cannot initialize payment element because the payment client secret is nil: ${partnerOffering?.name}, slug: ${partnerOffering?.slug}`
        );
      });
    }
  }, [quote, isGoogleApplePayEmbedInCheckoutPage, partnerOffering]);

  const handleSubmit = useCallback(() => {
    if (isEnhanceCheckoutUXFlagEnabled) {
      fadeInErrors();

      // scrollIntoView in async callback because submit may stop the scroll
      setTimeout(() => {
        errorElementRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
    }

    const valid = validateOrderParams();

    analytics.event(
      "Checkout Submit",
      {
        Errors: form.errors,
        Values: form.values,
        "Order Params": orderParams,
        "Promo Code": orderParams.promoCode,
        "Payment Type": paymentType,
        Valid: valid,
        "Has Form Errors": hasFormErrors,
        "Has API Error": false,
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
    if (!valid || emailError) return;

    analytics.event(
      "Checkout Submit Send",
      {
        Values: form.values,
        "Order Params": orderParams,
        "Promo Code": orderParams.promoCode,
        "Payment Type": paymentType,
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
    submit();

    if (
      appleFixFeatureFlag &&
      !hasFormErrors &&
      (paymentType.includes("apple") || paymentType.includes("google_pay"))
    ) {
      paymentElementRef.current?.submitPayment();
    }
  }, [
    isEnhanceCheckoutUXFlagEnabled,
    validateOrderParams,
    analytics,
    form.errors,
    form.values,
    orderParams,
    paymentType,
    hasFormErrors,
    emailError,
    submit,
    appleFixFeatureFlag,
    fadeInErrors,
  ]);

  const handlePaymentPaid = useCallback(
    (order: Order) => {
      trackMembershipPurchase(order, membership, location, partnerOffering);

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    },
    [partnerOffering, history, membership, location, trackMembershipPurchase]
  );

  const handlePaymentError = useCallback(
    (errorMessage: string) => {
      const replay = Sentry.getReplay();
      replay?.start();

      // This is a duplicate event to above, but should make it easier to track errors on Mixpanel
      // Depending on usage, we may want to remove the above event
      analytics.event(
        "Checkout Payment Error",
        {
          "Error Message": errorMessage,
          Values: values,
          "Order Params": orderParams,
          "Payment Type": paymentType,
          "Order ID": order?.id,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
      toast.error({ message: errorMessage });
      setPaymentProcessing(false);
    },
    [analytics, order?.id, paymentType, toast, values, orderParams]
  );

  const handleStartPollingOrder = useCallback(async () => {
    await new Promise<void>((resolve) => {
      let count = 0;

      // Start polling
      const interval = setInterval(async () => {
        // poll for maximum 60 seconds
        if (orderRef.current && count < STRIPE_POLLING_TIMEOUT_IN_SECOND) {
          const polledOrder = await getOrder(orderRef.current.id);
          count++;
          if (
            (polledOrder.status === OrderStatus.Paid ||
              polledOrder.status === OrderStatus.Confirmed) &&
            polledOrder.orderItems.length > 0
          ) {
            clearInterval(interval);
            // Even though we clear interval, async nature may still call this function
            // multiple times as response is returned
            if (!completedRef.current) {
              handlePaymentPaid(polledOrder);
              completedRef.current = true;
            }
            resolve();
          }
        } else {
          clearInterval(interval);
          handlePaymentError(fbt("Could not get order confirmation"));
          resolve();
        }
      }, 1000);
    });
  }, [getOrder, handlePaymentPaid, handlePaymentError, fbt]);

  useEffect(() => {
    if (
      (order !== prevOrder || (order && googleApplePayPaymentSucceeded)) &&
      isGoogleApplePayEmbedInCheckoutPage &&
      paymentElementRef.current
    ) {
      setPaymentProcessing(true);
      if (appleFixFeatureFlag) {
        if (
          !paymentType.includes("apple") &&
          !paymentType.includes("google_pay")
        ) {
          paymentElementRef.current.submitPayment();
        } else if (googleApplePayPaymentSucceeded) {
          // in the scenario where apple/google pay is selected while 100% promo is applied, we need to wait for the payment to be completed
          // before we start polling the order. it is done within `handlePaymentSuccess`.
          // for normal situations, we want to poll the order immediately when received
          handleStartPollingOrder();
        }
      } else {
        paymentElementRef.current.submitPayment();
      }
    }
  }, [
    order,
    prevOrder,
    paymentType,
    isGoogleApplePayEmbedInCheckoutPage,
    appleFixFeatureFlag,
    handleStartPollingOrder,
    quotePositivePrice,
    googleApplePayPaymentSucceeded,
  ]);

  const handlePaymentSuccess = useCallback(async () => {
    analytics.event(
      "Checkout Payment Success",
      {
        "Quote ID": quote?.id,
        "Total Price": quote?.totalPrice,
        Currency: quote?.currency,
        "Payment Type": paymentType,
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
    if (appleFixFeatureFlag) {
      if (
        !paymentType.includes("apple") &&
        !paymentType.includes("google_pay")
      ) {
        await handleStartPollingOrder();
        // in the case 100% promo was applied, we want to rely on payment success event rather than order being created
        // because order is instantly created and we need to wait for the payment to be completed
      } else if (!quotePositivePrice) {
        await handleStartPollingOrder();
      } else {
        setGoogleApplePayPaymentSucceeded(true);
      }
    } else {
      await handleStartPollingOrder();
    }
  }, [
    analytics,
    appleFixFeatureFlag,
    handleStartPollingOrder,
    quote,
    paymentType,
    quotePositivePrice,
  ]);

  const disabledSubmit = useMemo(() => {
    return disabledCheckout || submitting || !quote;
  }, [disabledCheckout, quote, submitting]);

  return (
    <ContactFormProvider
      partnerID={partnerOffering.partner.id}
      messageableID={partnerOfferingSlug}
      messageableType={OfferingMessageType.PartnerOffering}
      hubspotForm={hubspotForm}
    >
      <Helmet>
        <title>
          {offeringName} - {partnerOffering.partner.name}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      {/* NAVBAR */}
      <OfferingHeader
        logo={partnerOffering.partner.logoUrl}
        homepageURL={partnerOffering.partner.homepageUrl}
        offeringID={partnerOffering.id}
        partnerID={partnerOffering.partner.id}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        talkWithUsMessage={talkWithUsMessage}
        localeOptions={localeOptions}
        hubspotForm={hubspotForm}
        hubspotForms={hubspotForms}
      />
      <div className="container">
        {/* BACK BUTTON */}
        {onPressBack && (
          <div className="py-4">
            <BackButton onPress={onPressBack} />
            <div className="d-block d-md-none">
              <h3>{fbt("Confirm and pay")}</h3>
            </div>
            <div className="d-none d-md-block">
              <Spacer size={46} />
            </div>
          </div>
        )}
        {!!quote?.promotionLine && (
          <div
            className="p-3 fw-semibold d-flex align-items-center gap-1 rounded mb-4"
            style={{
              background: colors.spinachMinus80,
              color: colors.spinachCore,
            }}
          >
            <IoIosCheckmarkCircleOutline size={16} />
            {fbt("Your promo code has been applied!")}
          </div>
        )}
        <div className="row">
          {/* DESKTOP LEFT SIDE */}
          <div className="col-12 col-md-7 col-xl-8 order-2 order-md-1 mt-5 mt-md-0">
            {/* GUEST DETAILS */}
            <form>
              <GuestDetails
                onChangeEmail={onChangeEmail}
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                emailError={emailError}
                setEmailError={setEmailError}
                ref={errorElementRef}
                errorFieldId={errorFieldId}
                onErrorTextRef={handleErrorTextRef}
                onInputBorderRef={handleInputBorderRef}
              />
              {queryParams.fromCatalog === "equinox" &&
                MALIN_PARTNER_UUIDS.includes(partnerOffering.partner.id) && (
                  <EquinoxMembershipNumber
                    onChangeMembershipNumber={(value) =>
                      setFieldValue("membershipNumber", value)
                    }
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    setFieldError={setFieldError}
                    onErrorTextRef={handleErrorTextRef}
                    onInputBorderRef={handleInputBorderRef}
                    errorFieldId={errorFieldId}
                    ref={errorElementRef}
                  />
                )}
              <Spacer size={32} />

              <Fragment>
                {/* BILLING DETAILS */}
                <BillingDetails
                  askForTax={askForTax}
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                  onCheckContactDetailsSameAsBilling={
                    onCheckContactDetailsSameAsBilling
                  }
                  partnerId={partnerOffering.partner.id}
                  ref={errorElementRef}
                  errorFieldId={errorFieldId}
                  setFieldTouched={setFieldTouched}
                  onErrorTextRef={handleErrorTextRef}
                  onInputBorderRef={handleInputBorderRef}
                />
                <Spacer size={32} />
              </Fragment>
            </form>

            {showPaymentForm(quote) &&
              (includeAllPaymentsFeatureFlag ? (
                <Payments
                  partnerConfig={partnerConfig}
                  order={order}
                  partnerOffering={partnerOffering}
                  form={form}
                  quote={quote}
                  orderParams={orderParams}
                  location={location}
                  onPaymentClose={onPaymentClose}
                  paymentType={paymentType}
                  onChangePaymentType={setPaymentType}
                  onPaymentProcessing={setPaymentProcessing}
                  onErrorTextRef={handleErrorTextRef}
                  paymentProcessing={paymentProcessing}
                  errorElementRef={errorElementRef}
                  membership={membership}
                />
              ) : (
                <Fragment>
                  {isGoogleApplePayEmbedInCheckoutPage && (
                    <Fragment>
                      <h3>{fbt("Payment details")}</h3>
                      {quote ? (
                        quote.paymentClientSecret ? (
                          <div
                            ref={
                              errorFieldId === "paymentComplete"
                                ? errorElementRef
                                : undefined
                            }
                          >
                            <StripeProviderV2
                              stripePublishableKey={
                                paymentConfig?.stripePublishableKey
                              }
                              clientSecret={quote?.paymentClientSecret}
                              partnerSlug={partnerConfig.slug}
                            >
                              <PaymentElementInput
                                quote={quote}
                                redirectUrl={redirectUrl}
                                onSuccess={handlePaymentSuccess}
                                paymentProcessing={paymentProcessing}
                                onProcessing={() => setPaymentProcessing(true)}
                                onChangePaymentType={setPaymentType}
                                onError={(errorMessage) =>
                                  handlePaymentError(errorMessage)
                                }
                                billingInfo={values}
                                ref={paymentElementRef}
                                setFieldValue={setFieldValue}
                                partnerId={partnerOffering.partner.id}
                              />
                            </StripeProviderV2>
                            {errors.paymentComplete && touched.paymentComplete && (
                              <Form.Text
                                className="text-danger"
                                ref={(ref: HTMLElement | null) =>
                                  handleErrorTextRef(ref, "paymentComplete")
                                }
                              >
                                {errors.paymentComplete}
                              </Form.Text>
                            )}
                          </div>
                        ) : (
                          <span>
                            {fbt(
                              "This crash has been reported to us and we will work on resolving the issue as quickly as possible."
                            )}
                          </span>
                        )
                      ) : (
                        <Container>
                          <Row justifyContent="center">
                            <PulseLoader />
                          </Row>
                        </Container>
                      )}
                      <Spacer size={40} />
                    </Fragment>
                  )}
                </Fragment>
              ))}
            {membership && membershipPolicy?.cancellationText && (
              <Container>
                <CancellationPolicy html={membershipPolicy?.cancellationText} />
              </Container>
            )}
            {(membershipPolicy?.terms || termsURL) && (
              <div className="divider" />
            )}
            {membershipPolicy?.marketingConsent && !membershipPolicy?.terms && (
              <>
                <Spacer size={29} />
                <MarketingConsent
                  marketingConsent={membershipPolicy?.marketingConsent}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </>
            )}

            {/* TERMS AND CONDITIONS */}
            {membershipPolicy?.terms ? (
              <PaymentCTAWithMembershipTerms
                terms={membershipPolicy.terms}
                termsRequired={membershipPolicy.termsRequired}
                marketingConsent={membershipPolicy.marketingConsent}
                marketingConsentValues={values}
                errors={errors}
                values={values}
                setFieldValue={setFieldValue}
                onSubmit={handleSubmit}
                disabled={disabledSubmit}
                onTermsClick={onTermsClick}
                touched={touched}
                setFieldTouched={setFieldTouched}
                onErrorTextRef={handleErrorTextRef}
                onInputBorderRef={handleInputBorderRef}
                membershipPaymentCTALabel={
                  partnerConfig.membershipPaymentCTALabel
                }
              />
            ) : (
              <PaymentCTA
                isPaymentRequired={quotePositivePrice}
                isContinueToPayment={
                  !isGoogleApplePayEmbedInCheckoutPage ||
                  paymentModalFeatureFlag
                }
                partnerName={partnerOffering.partner.name}
                termsURL={termsURL}
                onSubmit={handleSubmit}
                disabled={disabledSubmit}
                membership={true}
                onTermsClick={onTermsClick}
                membershipPaymentCTALabel={
                  partnerConfig.membershipPaymentCTALabel
                }
              />
            )}
          </div>
          {/* DESKTOP RIGHT SIDE */}
          <div
            className={`col-12 col-md-5 col-xl-4 order-1 order-md-2
                              ${mq.device === "mobile" ? "px-0" : ""}`}
          >
            {desktopCheckoutBox}
          </div>
        </div>
      </div>
      {!includeAllPaymentsFeatureFlag && (
        <Modal
          show={isPaymentElementOpen}
          centered
          size="lg"
          backdrop="static"
          onHide={() => {
            if (paymentProcessing) {
              return;
            }

            onPaymentClose();
          }}
          className="header-center"
        >
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">{fbt("Payment")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {order && (
              <>
                {paymentProcessing && (
                  <Container center>
                    <span className="pulse_loading_description">
                      {fbt("Submitting your request")}
                    </span>
                    <Spacer size={16} />
                    <PulseLoader />
                    <Spacer size={16} />
                  </Container>
                )}
                <StripeProviderV2
                  stripePublishableKey={paymentConfig?.stripePublishableKey}
                  clientSecret={order.stripeClientSecret}
                  partnerSlug={partnerConfig.slug}
                >
                  <PaymentElementInput
                    quote={quote}
                    redirectUrl={redirectUrl}
                    onSuccess={handlePaymentSuccess}
                    paymentProcessing={paymentProcessing}
                    onChangePaymentType={setPaymentType}
                    onProcessing={() => setPaymentProcessing(true)}
                    onError={(errorMessage) => handlePaymentError(errorMessage)}
                    billingInfo={values}
                    setFieldValue={setFieldValue}
                    partnerId={partnerOffering.partner.id}
                  />
                </StripeProviderV2>
              </>
            )}
          </Modal.Body>
        </Modal>
      )}
      {/* TALK TO US MOBILE */}
      <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none mt-4">
        {showTalkWithUsPrompt ? (
          <TalkWithUsPrompt
            partnerID={partnerOffering.partner.id}
            hubspotForm={hubspotForm}
            messageableID={partnerOffering.id}
            hubspotForms={hubspotForms}
          />
        ) : (
          talkWithUsMessage && `${talkWithUsMessage}`
        )}
      </div>

      <div className="container py-4">
        {/* FOOTER */}
        <FooterV2 instagramLink={instagramLink} />
      </div>
      <Hidden md>
        {mobileCheckoutBox}
        <Spacer size={40} />
      </Hidden>
      {(submitting || paymentProcessing) && <LoadingOverlay />}
    </ContactFormProvider>
  );
}
